import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import type { SelectOption } from '@/components/Select/Select'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getFilterValue } from '@/utils/get-filter-value'

import { roomOptionsQueryOptions } from '../queries/filters/roomOptionsQueryOptions'
import { getRoomTypeOptions } from '../utils/get-room-type-options'

const routeApi = getRouteApi('/_auth/rooms/')

const RoomListFilters = () => {
  const { user } = useAuthStore()
  const { t } = useTranslation('rooms')

  const { type, id } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const filters: Filter[] = [
    {
      label: t('label.room'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'ids',
        dataTestId: 'room-filter',
        multiple: true,
        queryOptions: search => roomOptionsQueryOptions({ search }),
        value: id,
        placeholder: t('label.room'),
        onChange: value => handleChangeFilter('id', value)
      }
    },
    {
      variant: 'multiselect',
      label: t('label.type'),
      filterProps: {
        id: 'type-filter',
        options: getRoomTypeOptions(),
        multiple: true,
        value: type,
        placeholder: t('label.type'),
        onChange: value => handleChangeFilter('type', value)
      }
    }
  ]

  const handleChangeFilter = (
    key: 'id' | 'type',
    value: SelectOption<string>[] | string[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const isSomeFilterSelected = checkIfFilterSelected(filters)

  return (
    <Filters
      filters={filters}
      onClearAll={handleClearAll}
      disabledClearAllButton={!isSomeFilterSelected}
      actionsRender={
        user?.isSuperAdmin ? (
          <Button asLink to="/rooms/add" icon={<AddIcon />}>
            {t('button.add-new-room')}
          </Button>
        ) : null
      }
    />
  )
}

export default RoomListFilters
